import React, { useState, useEffect, useRef } from 'react';
import { Imovel } from '../../types/Property';
import { PropertyCard } from '../Properties/PropertyCard';
import './SearchResults.css';

interface SearchResultsProps {
  imoveis: Imovel[];
  loading?: boolean;
  totalResults?: number;
  location?: string;
  onPropertyClick?: (imovel: Imovel) => void;
  onFavorite?: (imovel: Imovel) => void;
  sortOrder?: 'relevantes' | 'maior-valor' | 'menor-valor';
  onSortChange?: (order: 'relevantes' | 'maior-valor' | 'menor-valor') => void;
  activeTab?: 'alugar' | 'comprar';
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  imoveis,
  loading = false,
  totalResults = 0,
  location = '',
  onPropertyClick,
  onFavorite,
  sortOrder = 'relevantes',
  onSortChange,
  activeTab = 'alugar',
}) => {
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [displayedCount, setDisplayedCount] = useState(6);
  const sortContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDisplayedCount(6);
  }, [imoveis.length, sortOrder]);

  const getSortLabel = (order: 'relevantes' | 'maior-valor' | 'menor-valor') => {
    switch (order) {
      case 'maior-valor':
        return 'Maior para Menor Valor';
      case 'menor-valor':
        return 'Menor para Maior Valor';
      case 'relevantes':
      default:
        return 'Mais Relevantes';
    }
  };

  const handleSortChange = (order: 'relevantes' | 'maior-valor' | 'menor-valor') => {
    onSortChange?.(order);
    setShowSortDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortContainerRef.current && !sortContainerRef.current.contains(event.target as Node)) {
        setShowSortDropdown(false);
      }
    };

    if (showSortDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSortDropdown]);
  if (loading) {
    return (
      <div className="search-results">
        <div className="results-loading">
          <div className="loading-spinner"></div>
          <p>Buscando imóveis...</p>
        </div>
      </div>
    );
  }

  if (imoveis.length === 0) {
    return (
      <div className="search-results">
        <div className="results-empty">
          <div className="empty-icon">
            
          </div>
          <h3>Nenhum imóvel encontrado</h3>
          <p>Tente ajustar os filtros de busca para encontrar mais opções.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="search-results">
      <div className="results-header">
        <h2 className="results-title">
          {totalResults === 1 ? '1 imóvel' : `${totalResults} imóveis`} {activeTab === 'alugar' ? 'para alugar' : 'para comprar'}{location ? ` em ${location}` : ''}
        </h2>
        <div className="results-sort-container" ref={sortContainerRef}>
          <div 
            className="results-sort" 
            onClick={() => setShowSortDropdown(!showSortDropdown)}
          >
            <span className="sort-label">{getSortLabel(sortOrder)}</span>
            <span className="sort-arrow">↕</span>
          </div>
          {showSortDropdown && (
            <div className="sort-dropdown">
              <button
                className={`sort-option ${sortOrder === 'relevantes' ? 'active' : ''}`}
                onClick={() => handleSortChange('relevantes')}
              >
                Mais Relevantes
              </button>
              <button
                className={`sort-option ${sortOrder === 'maior-valor' ? 'active' : ''}`}
                onClick={() => handleSortChange('maior-valor')}
              >
                Maior para Menor Valor
              </button>
              <button
                className={`sort-option ${sortOrder === 'menor-valor' ? 'active' : ''}`}
                onClick={() => handleSortChange('menor-valor')}
              >
                Menor para Maior Valor
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="results-grid">
        {imoveis.slice(0, displayedCount).map((imovel) => (
          <PropertyCard
            key={imovel.id}
            imovel={imovel}
            onClick={onPropertyClick}
          />
        ))}
      </div>

      {/* Carregar mais */}
      {displayedCount < imoveis.length && (
        <div className="load-more">
          <button 
            className="load-more-btn"
            onClick={() => setDisplayedCount(prev => prev + 6)}
          >
            Carregar mais imóveis
          </button>
        </div>
      )}
    </div>
  );
};
